/**
 * ChatLinkPreview component for the Indigo design system.
 *
 * A breadcrumb-style inline element used inside chat surfaces to render a
 * clickable path such as `Martinez Anderson QME / Uploads`. Each segment
 * is rendered as an independent link/button that can navigate to its own
 * destination.
 *
 * Visually a sibling to `ChatInlineElement`, but with a white (gray-0)
 * background and a leading link icon. Each segment exposes its own hover
 * highlight (`--indigo-gray-50`).
 *
 * @example
 * ```tsx
 * <ChatLinkPreview
 *   segments={[
 *     { label: "Martinez Anderson QME", href: "/cases/123" },
 *     { label: "Uploads", onClick: () => openUploads() },
 *   ]}
 * />
 * ```
 */
import { clsx } from "clsx";
import { Fragment, forwardRef, MouseEvent, ReactNode } from "react";
import { Icon } from "../../Icons";
import { Body } from "../../Typography/Body";
import "./ChatLinkPreview.css";

export interface ChatLinkPreviewSegment {
  /** Visible label for the segment. */
  label: string;
  /** Optional href. When present the segment renders as an `<a>`. */
  href?: string;
  /** Optional click handler. Mutually compatible with `href`. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Disable the segment (rendered as plain text in tertiary color). */
  disabled?: boolean;
  /** Test id for the segment. */
  "data-testid"?: string;
}

export interface ChatLinkPreviewProps {
  /** Ordered list of breadcrumb segments. */
  segments: ChatLinkPreviewSegment[];
  /** Custom leading icon. Defaults to the `link` icon at size xs. */
  icon?: ReactNode;
  /** Separator between segments. Defaults to `"/"`. */
  separator?: ReactNode;
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

export const ChatLinkPreview = forwardRef<
  HTMLSpanElement,
  ChatLinkPreviewProps
>(
  (
    { segments, icon, separator = "/", className, "data-testid": dataTestId },
    ref,
  ) => {
    const iconNode = icon ?? <Icon name="link" size="xs" />;

    return (
      <span
        ref={ref}
        className={clsx("indigo-chat-link-preview", className)}
        data-testid={dataTestId}
      >
        <span className="indigo-chat-link-preview__icon">{iconNode}</span>
        <span className="indigo-chat-link-preview__path">
          {segments.map((segment, idx) => {
            const isLast = idx === segments.length - 1;
            const segmentClass = "indigo-chat-link-preview__segment";
            const segmentTestId = segment["data-testid"];

            const labelEl = (
              <Body as="span" size="sm">
                {segment.label}
              </Body>
            );

            let segmentNode: ReactNode;
            if (segment.disabled) {
              segmentNode = (
                <span
                  className={segmentClass}
                  aria-disabled
                  data-testid={segmentTestId}
                  // `disabled` attr is rendered to drive the disabled style
                  {...({ disabled: true } as object)}
                >
                  {labelEl}
                </span>
              );
            } else if (segment.href) {
              segmentNode = (
                <a
                  className={segmentClass}
                  href={segment.href}
                  onClick={segment.onClick}
                  data-testid={segmentTestId}
                >
                  {labelEl}
                </a>
              );
            } else {
              segmentNode = (
                <button
                  type="button"
                  className={segmentClass}
                  onClick={segment.onClick}
                  data-testid={segmentTestId}
                >
                  {labelEl}
                </button>
              );
            }

            return (
              <Fragment key={`${segment.label}-${idx}`}>
                {segmentNode}
                {!isLast && (
                  <span
                    className="indigo-chat-link-preview__separator"
                    aria-hidden
                  >
                    {separator}
                  </span>
                )}
              </Fragment>
            );
          })}
        </span>
      </span>
    );
  },
);

ChatLinkPreview.displayName = "ChatLinkPreview";

export default ChatLinkPreview;
